import * as luxon from 'luxon';

function isoStringOrDate(value: string | Date): luxon.DateTime {
  if (value instanceof Date) {
    return luxon.DateTime.fromJSDate(value);
  }
  return luxon.DateTime.fromISO(value);
}

export function toMediumDateTime(date: Date | string): string {
  return isoStringOrDate(date).toLocaleString(luxon.DateTime.DATETIME_MED);
}

export function toMediumDate(date: Date | string): string {
  return isoStringOrDate(date).toLocaleString(luxon.DateTime.DATE_MED);
}

// Compact enough for a chart axis, where the year is implied by the range.
export function toMonthDay(date: Date | string): string {
  return isoStringOrDate(date).toLocaleString({
    month: 'numeric',
    day: 'numeric',
  });
}

export function toLongDateTime(date: Date | string): string {
  return isoStringOrDate(date).toLocaleString({
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}

// The `value` formats of `<input type="date">` and `<input type="datetime-local">`.
// Neither carries a time zone, so these convert at the edge: a `Date` is an
// instant, and the field shows the wall-clock date and time of that instant in
// `zone`. Without a `zone`, that is luxon's default, the browser's own zone.
const DATE_INPUT_FORMAT = 'yyyy-MM-dd';
const DATE_TIME_INPUT_FORMAT = "yyyy-MM-dd'T'HH:mm";

const zoneOptions = (zone: string | undefined) => (zone ? {zone} : {});

/** The `value` of an `<input type="date">` that shows the day of `date`. */
export function toDateInputValue(date: Date, zone?: string): string {
  return luxon.DateTime.fromJSDate(date, zoneOptions(zone)).toFormat(
    DATE_INPUT_FORMAT,
  );
}

/**
 * The `value` of an `<input type="datetime-local">` that shows `date`, to the
 * minute: the field's default step.
 */
export function toDateTimeInputValue(date: Date, zone?: string): string {
  return luxon.DateTime.fromJSDate(date, zoneOptions(zone)).toFormat(
    DATE_TIME_INPUT_FORMAT,
  );
}

/**
 * The start of the day that an `<input type="date">` value names, or `null` if
 * the value is empty or not a date.
 */
export function fromDateInputValue(value: string, zone?: string): Date | null {
  const parsed = luxon.DateTime.fromFormat(
    value,
    DATE_INPUT_FORMAT,
    zoneOptions(zone),
  );
  return parsed.isValid ? parsed.toJSDate() : null;
}

/**
 * The instant that an `<input type="datetime-local">` value names, or `null` if
 * the value is empty or not a date and time. A time that the zone skips (the
 * hour a clock springs forward) moves forward by that hour, and a time that
 * happens twice (the hour a clock falls back) is the first of the two.
 */
export function fromDateTimeInputValue(
  value: string,
  zone?: string,
): Date | null {
  // The field's value can carry seconds when its step is under a minute.
  if (!/^\d{4,}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?$/.test(value)) {
    return null;
  }
  const parsed = luxon.DateTime.fromISO(value, zoneOptions(zone));
  return parsed.isValid ? parsed.toJSDate() : null;
}

/**
 * The `value` of an `<input type="time">` that shows the time of day of `date`
 * in `zone`, to the minute: the field's default step.
 */
export function toTimeInputValue(date: Date, zone?: string): string {
  return luxon.DateTime.fromJSDate(date, zoneOptions(zone)).toFormat('HH:mm');
}

/**
 * The time of day that an `<input type="time">` value names, as `HH:mm` on a
 * 24-hour clock, or `null` if the value is empty or not a time. The field
 * reports seconds when its step is under a minute. They are dropped, since a
 * time of day is kept to the minute.
 *
 * A time of day is not an instant, so it stays a string: it names a time on
 * the clock, and which instant that is depends on the day and the zone.
 */
export function fromTimeInputValue(value: string): string | null {
  const match = /^([01]\d|2[0-3]):([0-5]\d)(:[0-5]\d(\.\d{1,3})?)?$/.exec(
    value,
  );
  return match ? `${match[1]}:${match[2]}` : null;
}

/**
 * The first instant after `after` when the clock in `zone` shows `time`
 * (`HH:mm`): later the same day if that time is still to come, or else the
 * next day. Without a `zone`, that is the browser's zone. `null` if `time` is
 * not a time of day.
 *
 * It follows the clock, not a count of hours, so 3 PM stays 3 PM on the day
 * the clocks change. A time that the clock skips that day (2:30 AM, when it
 * springs forward from 2:00 to 3:00) does not happen, so the result is that
 * time on the next day. A time that the clock shows twice (1:30 AM, when it
 * falls back) is the first of the two that is still to come. Either way, a
 * field that shows the result in that zone shows `time`.
 */
export function nextOccurrenceOfTime(
  time: string,
  after: Date,
  zone?: string,
): Date | null {
  const parsed = fromTimeInputValue(time);
  if (!parsed) {
    return null;
  }
  const [hour, minute] = parsed.split(':').map(Number);
  const start = luxon.DateTime.fromJSDate(after, zoneOptions(zone));
  // A day where the time is skipped is followed by one where it is not, so
  // this looks no further than the day after tomorrow.
  for (let days = 0; days <= 2; days++) {
    const onDay = start
      .plus({days})
      .set({hour, minute, second: 0, millisecond: 0});
    const instants = onDay
      .getPossibleOffsets()
      // In a skipped hour, luxon moves the time forward. That is not `time`.
      .filter(instant => instant.hour === hour && instant.minute === minute)
      .map(instant => instant.toMillis())
      .sort((a, b) => a - b);
    const next = instants.find(ms => ms > after.getTime());
    if (next != null) {
      return new Date(next);
    }
  }
  return null;
}

export function isAfter(
  isThis: string | Date,
  afterThis: string | Date,
): boolean {
  return isoStringOrDate(isThis) > isoStringOrDate(afterThis);
}

export function isInTheFuture(date: string | Date): boolean {
  return isAfter(date, new Date());
}

export function fromNow(date: string | Date): string {
  return isoStringOrDate(date).toRelative()!;
}

// Like `fromNow`, but snapped to calendar units, so the recent past reads
// "yesterday"/"today" instead of "23 hours ago". Lowercase, as luxon returns it.
export function fromNowCalendar(date: string | Date): string {
  return isoStringOrDate(date).toRelativeCalendar()!;
}

export function haveSameTimeComponent(
  date1: string | Date,
  date2: string | Date,
): boolean {
  const a = isoStringOrDate(date1);
  const b = isoStringOrDate(date2);
  return (
    a.hour === b.hour &&
    a.minute === b.minute &&
    a.second === b.second &&
    a.millisecond === b.millisecond
  );
}

export function replaceTimeComponent(
  ofDate: string | Date,
  withTimeFrom: string | Date,
): Date {
  const b = isoStringOrDate(withTimeFrom);
  return isoStringOrDate(ofDate)
    .set({
      hour: b.hour,
      minute: b.minute,
      second: b.second,
      millisecond: b.millisecond,
    })
    .toJSDate();
}
